import { useEffect, useState } from 'preact/hooks';
import { useI18n } from '../i18n';
import { RequireAdmin } from '../components/RequireAdmin';
import { useAuth } from '../lib/auth';
import {
  getTournament,
  putTournament,
  type PredictionsVisibility,
  type TournamentView,
} from '../lib/admin';

const CODE = 'wc2026';

export function AdminTournament() {
  const { localePath } = useI18n();
  const { accessToken } = useAuth();
  const [t, setT] = useState<TournamentView | null>(null);
  const [status, setStatus] = useState<TournamentView['status']>('upcoming');
  const [visibility, setVisibility] = useState<PredictionsVisibility>('auto_hide_in_knockouts');
  const [counterVisible, setCounterVisible] = useState<boolean>(true);
  const [partnersRevealed, setPartnersRevealed] = useState<boolean>(true);
  const [forceLitres, setForceLitres] = useState<boolean>(false);
  const [forceLitresValue, setForceLitresValue] = useState<string>('');
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  async function load() {
    try {
      const res = await getTournament(CODE);
      setT(res);
      setStatus(res.status);
      setVisibility(res.predictionsVisibility);
      setCounterVisible(res.prizeCounterVisible);
      setPartnersRevealed(res.partnersRevealed);
      setForceLitres(res.prizeLitresOverride != null);
      setForceLitresValue(res.prizeLitresOverride != null ? String(res.prizeLitresOverride) : '');
    } catch (e: any) {
      setErr(`${e?.message || e}`);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function submit(e: Event) {
    e.preventDefault();
    if (!accessToken) return;
    setErr(null);
    setSaved(false);
    // Guard against a "force" toggle left on with an empty/invalid number.
    let override: number | null = null;
    if (forceLitres) {
      const n = Number(forceLitresValue);
      if (!Number.isFinite(n) || n < 0 || forceLitresValue.trim() === '') {
        setErr('Enter a valid litres number (0 or more) to force the counter.');
        return;
      }
      override = Math.round(n);
    }
    setSaving(true);
    try {
      const res = await putTournament(accessToken, CODE, {
        status,
        predictionsVisibility: visibility,
        prizeCounterVisible: counterVisible,
        partnersRevealed,
        prizeLitresOverride: override,
      });
      setT(res);
      setSaved(true);
    } catch (e: any) {
      setErr(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <RequireAdmin>
      <div class="admin-page">
        <div class="admin-shell">
          <header class="admin-header">
            <a class="admin-back" href={localePath('/admin')}>← Admin</a>
            <h1>Tournament — {t?.name ?? CODE}</h1>
          </header>

          <p class="admin-lede">
            Tournament status and the litres-in-game counter that surfaces on
            the website landing and inside the app. By default it's the total
            litres pledged by partners; you can force it to a fixed number.
          </p>

          {!t && !err && <p class="admin-lede">Loading…</p>}

          {t && (
            <form onSubmit={submit} class="admin-form">
              <div class="admin-field">
                <span>Litres in game</span>
                <dl class="admin-stats">
                  <div class="admin-stat admin-stat--total">
                    <dt>Total</dt>
                    <dd>
                      {t.prizeLitres == null ? '—' : formatLitres(t.prizeLitres)}
                      {t.prizeLitresOverride != null ? ' (forced)' : ' (auto)'}
                    </dd>
                  </div>
                </dl>
                <label style={{ flexDirection: 'row', alignItems: 'flex-start', gap: '8px', display: 'flex', marginTop: 8 }}>
                  <input
                    type="checkbox"
                    checked={forceLitres}
                    onChange={(e) => setForceLitres((e.target as HTMLInputElement).checked)}
                    style={{ marginTop: 3 }}
                  />
                  <span style={{ textTransform: 'none', letterSpacing: 0, color: '#f5ede0', fontFamily: 'inherit' }}>
                    Force the number
                    <span style={{ display: 'block', fontSize: 11, color: 'rgba(245,237,224,0.55)', marginTop: 2 }}>
                      The public counter shows exactly this many litres instead of
                      the partner total. Uncheck to go back to the automatic total.
                    </span>
                  </span>
                </label>
                {forceLitres && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: 8 }}>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={forceLitresValue}
                      onInput={(e) => setForceLitresValue((e.target as HTMLInputElement).value)}
                      placeholder="e.g. 500"
                      style={{ maxWidth: 140 }}
                    />
                    <span style={{ color: 'rgba(245,237,224,0.7)', fontSize: 13 }}>L shown publicly</span>
                  </div>
                )}
              </div>

              <label class="admin-field" style={{ flexDirection: 'row', alignItems: 'flex-start', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={counterVisible}
                  onChange={(e) => setCounterVisible((e.target as HTMLInputElement).checked)}
                  style={{ marginTop: 3 }}
                />
                <span style={{ textTransform: 'none', letterSpacing: 0, color: '#f5ede0', fontFamily: 'inherit' }}>
                  Show prize counter publicly
                  <span style={{ display: 'block', fontSize: 11, color: 'rgba(245,237,224,0.55)', marginTop: 2 }}>
                    When off, the app's prize-pool strip replaces the live numbers with a "litres incoming" bubble animation. Use during testing so users don't see partial pledges.
                  </span>
                </span>
              </label>

              <label class="admin-field" style={{ flexDirection: 'row', alignItems: 'flex-start', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={partnersRevealed}
                  onChange={(e) => setPartnersRevealed((e.target as HTMLInputElement).checked)}
                  style={{ marginTop: 3 }}
                />
                <span style={{ textTransform: 'none', letterSpacing: 0, color: '#f5ede0', fontFamily: 'inherit' }}>
                  Partners revealed
                  <span style={{ display: 'block', fontSize: 11, color: 'rgba(245,237,224,0.55)', marginTop: 2 }}>
                    When off, the app's Partners tab swaps its list/map for a "soon to be announced" banner. Use pre-launch while the roster isn't ready for public reveal. The prize-pool strip is controlled separately by "Show prize counter publicly" above.
                  </span>
                </span>
              </label>

              <label class="admin-field">
                <span>Status</span>
                <select
                  value={status}
                  onChange={(e) => setStatus((e.target as HTMLSelectElement).value as TournamentView['status'])}
                >
                  <option value="upcoming">upcoming</option>
                  <option value="live">live</option>
                  <option value="done">done</option>
                </select>
              </label>

              <label class="admin-field">
                <span>Predictions visibility (cross-user)</span>
                <select
                  value={visibility}
                  onChange={(e) => setVisibility((e.target as HTMLSelectElement).value as PredictionsVisibility)}
                >
                  <option value="auto_hide_in_knockouts">
                    Auto — visible during group phase, hidden during knockouts
                  </option>
                  <option value="after_match_done">
                    After match done — only revealed once the score is in
                  </option>
                  <option value="always">
                    Always visible
                  </option>
                </select>
                <small style={{ color: 'rgba(245,237,224,0.55)', fontSize: 11, marginTop: 4 }}>
                  Users always see their own picks. This controls what other players see when viewing someone's profile from the leaderboard.
                </small>
              </label>

              <dl class="admin-meta">
                <div>
                  <dt>Code</dt>
                  <dd>{t.code}</dd>
                </div>
                <div>
                  <dt>Schedule</dt>
                  <dd>{t.startsAt} → {t.endsAt}</dd>
                </div>
              </dl>

              {err && <p class="admin-error">{err}</p>}
              {saved && <p class="admin-saved">Saved.</p>}

              <button type="submit" class="admin-primary" disabled={saving}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </form>
          )}
        </div>
      </div>
    </RequireAdmin>
  );
}

function formatLitres(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const fmt = new Intl.NumberFormat('en', {
    maximumFractionDigits: n % 1 === 0 ? 0 : 1,
  }).format(n);
  return `${fmt} L`;
}
